import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateInvoice, useUpdateInvoice, useNextInvoiceNumber } from '@/hooks/useInvoice';
import type { Invoice, InvoiceType, InvoiceLineItem, CreateInvoiceRequest } from '@/types/invoice';

interface InvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice?: Invoice; // If provided, edit mode
  defaultType?: InvoiceType;
  defaultFlowId?: string;
  defaultSourcePOId?: string;
  defaultSourceSOId?: string;
}

const InvoiceDialog: React.FC<InvoiceDialogProps> = ({
  isOpen,
  onClose,
  invoice,
  defaultType = 'purchase',
  defaultFlowId,
  defaultSourcePOId,
  defaultSourceSOId,
}) => {
  const { t } = useTranslation('psi');
  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice();
  const { data: nextNumber } = useNextInvoiceNumber();

  const isEdit = !!invoice;

  const [invoiceType, setInvoiceType] = useState<InvoiceType>(defaultType);
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [vendorCode, setVendorCode] = useState('');
  const [customerCode, setCustomerCode] = useState('');
  const [counterpartyName, setCounterpartyName] = useState('');
  const [currency, setCurrency] = useState('TWD');
  const [taxRate, setTaxRate] = useState(0.05);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<InvoiceLineItem[]>([
    { line_no: 1, product_name: '', specification: '', quantity: 1, unit_price: 0, amount: 0, currency: 'TWD' },
  ]);

  useEffect(() => {
    if (invoice) {
      setInvoiceType(invoice.invoice_type);
      setInvoiceDate(invoice.invoice_date ? new Date(invoice.invoice_date).toISOString().split('T')[0] : '');
      setDueDate(invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '');
      setVendorCode(invoice.vendor_code || '');
      setCustomerCode(invoice.customer_code || '');
      setCounterpartyName(invoice.counterparty_name || '');
      setCurrency(invoice.currency || 'TWD');
      setTaxRate(invoice.tax_rate || 0.05);
      setNotes(invoice.notes || '');
      setItems(invoice.items?.length > 0 ? invoice.items : [
        { line_no: 1, product_name: '', specification: '', quantity: 1, unit_price: 0, amount: 0, currency: 'TWD' },
      ]);
    } else {
      const today = new Date().toISOString().split('T')[0];
      const due = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
      setInvoiceDate(today);
      setDueDate(due);
      setInvoiceType(defaultType);
    }
  }, [invoice, defaultType]);

  const updateItem = (index: number, field: keyof InvoiceLineItem, value: string | number) => {
    const newItems = [...items];
    (newItems[index] as unknown as Record<string, unknown>)[field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }
    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      { line_no: items.length + 1, product_name: '', specification: '', quantity: 1, unit_price: 0, amount: 0, currency },
    ]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const taxAmount = subtotal * taxRate;
  const grandTotal = subtotal + taxAmount;

  const handleSubmit = async () => {
    if (isEdit && invoice) {
      await updateInvoice.mutateAsync({
        id: invoice.invoice_id,
        request: {
          invoice_date: invoiceDate,
          due_date: dueDate,
          vendor_code: vendorCode || undefined,
          customer_code: customerCode || undefined,
          counterparty_name: counterpartyName || undefined,
          items,
          currency,
          tax_rate: taxRate,
          notes: notes || undefined,
        },
      });
    } else {
      const req: CreateInvoiceRequest = {
        invoice_type: invoiceType,
        invoice_date: invoiceDate,
        due_date: dueDate,
        items,
        currency,
        tax_rate: taxRate,
      };
      if (vendorCode) req.vendor_code = vendorCode;
      if (customerCode) req.customer_code = customerCode;
      if (counterpartyName) req.counterparty_name = counterpartyName;
      if (defaultFlowId) req.flow_id = defaultFlowId;
      if (defaultSourcePOId) req.source_po_id = defaultSourcePOId;
      if (defaultSourceSOId) req.source_so_id = defaultSourceSOId;
      if (notes) req.notes = notes;
      await createInvoice.mutateAsync(req);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEdit ? t('invoice.dialog.editTitle', 'Edit Invoice') : t('invoice.dialog.createTitle', 'Create Invoice')}
            </h2>
            {!isEdit && nextNumber && (
              <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">{nextNumber}</span>
            )}
          </div>

          {/* Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.dialog.type', 'Type')}
              </label>
              <select
                value={invoiceType}
                onChange={(e) => setInvoiceType(e.target.value as InvoiceType)}
                disabled={isEdit}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              >
                <option value="purchase">Purchase (AP)</option>
                <option value="sales">Sales (AR)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {invoiceType === 'purchase' ? t('invoice.dialog.vendor', 'Vendor Code') : t('invoice.dialog.customer', 'Customer Code')}
              </label>
              <input
                type="text"
                value={invoiceType === 'purchase' ? vendorCode : customerCode}
                onChange={(e) => invoiceType === 'purchase' ? setVendorCode(e.target.value) : setCustomerCode(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('invoice.dialog.counterparty', 'Counterparty Name')}
            </label>
            <input
              type="text"
              value={counterpartyName}
              onChange={(e) => setCounterpartyName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.dialog.invoiceDate', 'Invoice Date')}
              </label>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.dialog.dueDate', 'Due Date')}
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('invoice.dialog.items', 'Line Items')}
              </label>
              <button onClick={addItem} className="text-xs text-indigo-600 hover:text-indigo-700">
                + {t('invoice.dialog.addItem', 'Add Item')}
              </button>
            </div>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Product Name"
                    value={item.product_name}
                    onChange={(e) => updateItem(idx, 'product_name', e.target.value)}
                    className="col-span-4 px-2 py-1.5 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                    className="col-span-2 px-2 py-1.5 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    value={item.unit_price}
                    onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                    className="col-span-3 px-2 py-1.5 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <span className="col-span-2 text-sm text-right text-gray-600 dark:text-gray-400">
                    {(item.quantity * item.unit_price).toLocaleString()}
                  </span>
                  <button
                    onClick={() => removeItem(idx)}
                    className="col-span-1 text-red-400 hover:text-red-600 text-center"
                    disabled={items.length <= 1}
                  >
                    <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t dark:border-gray-700 pt-3 space-y-1 text-sm text-right">
            <p className="text-gray-600 dark:text-gray-400">
              Subtotal: {currency} {subtotal.toLocaleString()}
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              Tax ({(taxRate * 100).toFixed(0)}%): {currency} {taxAmount.toLocaleString()}
            </p>
            <p className="font-semibold text-gray-900 dark:text-white">
              Grand Total: {currency} {grandTotal.toLocaleString()}
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('invoice.dialog.notes', 'Notes')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={createInvoice.isLoading || updateInvoice.isLoading}
              className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {createInvoice.isLoading || updateInvoice.isLoading ? '...' : isEdit ? t('common.save', 'Save') : t('common.create', 'Create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDialog;
